import { useState, useCallback, useEffect } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Upload,
  File as FileIcon,
  FileX,
  Check,
  Clock,
} from "@phosphor-icons/react";
import FilePreview from "@/components/custom/file-preview";
import { useTranslation } from "react-i18next";
import { Mixpanel } from "@/lib/mixpanel";
import { cn } from "@/lib/utils";
import { useUploadDocument } from "@/services/mutations/documents";
import { ApiResponse, DocumentType } from "@/lib/types";
import { api } from "@/lib/api";

type DocumentUploadFormValues = {
  name: string;
  type: string;
  files: File[];
};

const supportedDocumentMimeTypes = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/tiff",
]);

const supportedDocumentExtensions = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".tiff",
  ".tif",
];

const isSupportedDocumentFile = (file: File) => {
  const lowerCaseFileName = file.name.toLowerCase();

  return (
    supportedDocumentMimeTypes.has(file.type) ||
    supportedDocumentExtensions.some((extension) =>
      lowerCaseFileName.endsWith(extension)
    )
  );
};

// Status enum for tracking document processing
enum DocumentStatus {
  IDLE = "Idle",
  UPLOADING = "Uploading",
  PENDING = "Pending",
  PROCESSING = "Processing",
  PROCESSED = "Processed",
  FAILED = "Failed",
}

export function DocumentUploadModal({
  open,
  onOpenChange,
  onViewDocument,
  initialFiles,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewDocument: ({
    files,
    documentResult,
  }: {
    files: File[];
    documentResult: DocumentType;
  }) => void;
  initialFiles?: File[];
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<DocumentStatus>(DocumentStatus.IDLE);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [documentResult, setDocumentResult] = useState<DocumentType | null>(
    null
  );
  const [error, setError] = useState<string | undefined>();

  const documentUploadSchema = z.object({
    name: z.string().min(1, t("error_message.document_name_required")),
    type: z.enum(["Invoice", "Receipt"], {
      message: t("error_message.invalid_document_type"),
    }),
    files: z
      .array(z.instanceof(File))
      .min(1, t("error_message.at_least_one_file_required")),
  });

  const form = useForm<DocumentUploadFormValues>({
    resolver: zodResolver(documentUploadSchema),
    defaultValues: {
      name: "",
      type: "Invoice",
      files: [],
    },
  });

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0 && acceptedFiles.length === 0) {
        form.setError("files", {
          type: "manual",
          message: t("error_message.invalid_document_type"),
        });
        return;
      }

      if (acceptedFiles.length > 0) {
        const currentFiles = form.watch("files");

        // Only set the name if there are no files yet
        if (currentFiles.length === 0) {
          const fileName = acceptedFiles[0].name
            .split(".")
            .slice(0, -1)
            .join(".");
          form.setValue("name", fileName || acceptedFiles[0].name);
        }

        // Append new files to existing ones
        form.setValue("files", [...currentFiles, ...acceptedFiles]);
        form.clearErrors("files");
        form.clearErrors("name");
      }
    },
    [form, t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    validator: (file) => {
      if (isSupportedDocumentFile(file)) {
        return null;
      }

      return {
        code: "file-invalid-type",
        message: t("error_message.invalid_document_type"),
      };
    },
    multiple: true,
  });

  const { mutate: uploadDocument } = useUploadDocument({
    onSuccess: async ({ value }) => {
      setDocumentResult(value);
      setStatus(DocumentStatus.PENDING);

      pollDocumentStatus(value.id);

      if (value.status === DocumentStatus.FAILED) {
        setError(value.processingError);
        Mixpanel.track("Document Upload Failed");
      } else {
        Mixpanel.track("Document Upload Success");
      }
    },
    onError: () => {
      setStatus(DocumentStatus.FAILED);
    },
  });

  const onSubmit = async (values: DocumentUploadFormValues) => {
    setStatus(DocumentStatus.UPLOADING);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("type", values.type);

    values.files.forEach((file) => {
      formData.append("files", file);
    });

    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        const newProgress = prev + 5;
        if (newProgress >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return newProgress;
      });
    }, 100);

    uploadDocument(formData);

    setTimeout(() => {
      clearInterval(progressInterval);
      setUploadProgress(100);
    }, 2000);
  };

  const pollDocumentStatus = async (id: string) => {
    setTimeout(() => {
      setTimeout(async () => {
        const updatedDocumentResult =
          await getDocumentPerThreeSecondsTillProcessed(id);

        if (updatedDocumentResult) {
          setDocumentResult(updatedDocumentResult);
          setStatus(updatedDocumentResult.status as DocumentStatus);
        }

        if (updatedDocumentResult?.status === DocumentStatus.PROCESSED) {
          setStatus(DocumentStatus.PROCESSED);
          return;
        }

        if (updatedDocumentResult?.status === DocumentStatus.FAILED) {
          setStatus(DocumentStatus.FAILED);
          return;
        }
      }, 3000); // processing time
    }, 1000); // pending time
  };

  const getDocumentPerThreeSecondsTillProcessed = async (id: string) => {
    const {
      data: { value },
    } = await api.get<ApiResponse<DocumentType>>(`/documents/${id}`);

    setDocumentResult(value);
    setStatus(value.status as DocumentStatus);

    if (
      value.status === DocumentStatus.PROCESSED ||
      value.status === DocumentStatus.FAILED
    ) {
      return value;
    }

    setTimeout(() => {
      getDocumentPerThreeSecondsTillProcessed(id);
    }, 3000);
  };

  useEffect(() => {
    if (initialFiles) {
      const fileName = initialFiles[0].name.split(".").slice(0, -1).join(".");
      form.setValue("name", fileName || initialFiles[0].name);
      form.setValue("files", initialFiles);
      form.clearErrors("files");
      form.clearErrors("name");
    }
  }, [form, initialFiles]);

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          setTimeout(() => {
            form.reset();
            setStatus(DocumentStatus.IDLE);
            setUploadProgress(0);
            setDocumentResult(null);
          }, 500);
        }

        onOpenChange(open);
      }}
    >
      <DialogContent
        data-testid="document-upload-modal"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {status === DocumentStatus.IDLE
              ? t("title.upload_document")
              : t("title.document_status")}
          </DialogTitle>
        </DialogHeader>

        {status === DocumentStatus.IDLE && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* File dropzone */}
              <FormField
                control={form.control}
                name="files"
                render={() => (
                  <FormItem>
                    <FormLabel>{t("label.documents")}</FormLabel>
                    <FormControl>
                      <div
                        {...getRootProps()}
                        className={cn(
                          "cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors",
                          isDragActive
                            ? "border-primary bg-primary/5"
                            : "border-muted-foreground/30",
                          form.formState.errors.files ? "border-red-500" : ""
                        )}
                      >
                        <input
                          {...getInputProps()}
                          data-testid="upload-document-file-input"
                        />
                        {form.watch("files").length > 0 ? (
                          <FilePreview
                            files={form.watch("files")}
                            onRemove={(file) => {
                              form.setValue(
                                "files",
                                form.watch("files").filter((f) => f !== file)
                              );
                            }}
                          />
                        ) : (
                          <div className="flex flex-col items-center text-muted-foreground">
                            <Upload size={32} weight="thin" className="mb-2" />
                            <p className="text-sm">
                              {isDragActive
                                ? t("label.drop_files_here")
                                : t("label.drag_or_click")}
                            </p>
                            <p className="mt-1 text-xs">PDF, JPG, PNG, TIFF</p>
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Document name field */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("label.document_name")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        data-testid="upload-document-name-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Document type field */}
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("label.document_type")}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      data-testid="upload-document-type-select"
                    >
                      <FormControl>
                        <SelectTrigger data-testid="upload-document-type-select-trigger">
                          <SelectValue
                            placeholder={t("label.select_document_type")}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem
                          value="Invoice"
                          data-testid="upload-document-type-select-item-invoice"
                        >
                          {t("label.invoice")}
                        </SelectItem>
                        <SelectItem
                          value="Receipt"
                          data-testid="upload-document-type-select-item-receipt"
                        >
                          {t("label.receipt")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="submit"
                  disabled={form.formState.isSubmitting}
                  className="w-full"
                  data-testid="upload-document-submit-button"
                >
                  {form.formState.isSubmitting
                    ? t("button.uploading")
                    : t("button.upload")}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}

        {/* Upload progress bar */}
        {status === DocumentStatus.UPLOADING && (
          <div
            data-testid="upload-document-uploading-progress-bar"
            className="space-y-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-white">
                {t("label.uploading_document")}
              </span>
              <span className="text-sm text-white">{uploadProgress}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-500">
              <div
                className="h-full rounded-full bg-white"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {status !== DocumentStatus.IDLE && documentResult && (
          <div className="space-y-4">
            {/* Document details */}
            <div className="space-y-2 rounded-lg border border-white/10 bg-white/10 p-4">
              <div className="flex items-center gap-3">
                <FileIcon size={24} className="mt-0.5 shrink-0 text-white" />
                <div>
                  <h3 className="text-sm font-medium text-white">
                    {t("label.file_name")}: {documentResult.name}
                  </h3>
                  <p className="text-sm text-white">
                    {t("label.document_id")}: {documentResult.id}
                  </p>
                  <p className="text-xs text-white/60">
                    {t("label.uploaded_at")}:{" "}
                    {new Date(documentResult.uploadedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/10 p-4">
              {/* Pending Status */}
              {status === DocumentStatus.PENDING && (
                <div
                  className="flex items-center gap-3"
                  data-testid="upload-document-pending-indicator"
                >
                  <Clock size={24} className="animate-pulse text-amber-500" />
                  <div>
                    <p className="text-sm font-medium text-white">
                      {t("label.pending")}
                    </p>
                    <p className="text-xs text-white/60">
                      {t("label.document_pending_description")}
                    </p>
                  </div>
                </div>
              )}

              {/* Processing Status */}
              {status === DocumentStatus.PROCESSING && (
                <div
                  className="flex items-center gap-3"
                  data-testid="upload-document-processing-indicator"
                >
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <div>
                    <p className="text-sm font-medium text-white">
                      {t("label.processing")}
                    </p>
                    <p className="text-xs text-white/60">
                      {t("label.ocr_processing_description")}
                    </p>
                  </div>
                </div>
              )}

              {/* Processed Status */}
              {status === DocumentStatus.PROCESSED && (
                <div
                  className="flex items-center gap-3"
                  data-testid="upload-document-processed-indicator"
                >
                  <Check size={24} className="text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-white">
                      {t("label.processed")}
                    </p>
                    <p className="text-xs text-white/60">
                      {t("label.document_processed_description")}
                    </p>
                  </div>
                </div>
              )}

              {/* Failed Status */}
              {status === DocumentStatus.FAILED && (
                <div
                  className="flex items-center gap-3"
                  data-testid="upload-document-failed-indicator"
                >
                  <FileX size={24} className="text-red-500" />
                  <div>
                    <p className="text-sm text-white">{t("label.failed")}</p>
                    <p className="text-xs text-white/60">
                      {t("label.document_failed_description")}
                    </p>

                    {error && (
                      <div className="mt-2 flex flex-col">
                        <span className="text-sm font-medium text-red-400">
                          HATA:
                        </span>
                        <p className="text-xs text-white/60">{error}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex w-full gap-2">
              <DialogClose asChild>
                <Button
                  variant="outline"
                  className="w-full"
                  data-testid="upload-document-close-button"
                >
                  {t("button.close")}
                </Button>
              </DialogClose>

              {status === DocumentStatus.PROCESSED && (
                <Button
                  variant="default"
                  onClick={() => {
                    onViewDocument({
                      files: form.getValues("files") ?? [],
                      documentResult,
                    });
                  }}
                  className="w-full"
                  data-testid="upload-document-view-button"
                >
                  {t("button.view_document")}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
